import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  TextField,
  Typography,
} from "@mui/material";
import { motion } from "framer-motion";
import { useState } from "react";

/* ---------------- CONTACT ITEM ---------------- */

const ContactItem = ({ icon, title, description, href }) => (
  <Box sx={{ display: "flex", gap: 2.2, alignItems: "flex-start" }}>
    <Box
      sx={{
        width: 42,
        height: 42,
        borderRadius: "50%",
        background: "rgba(207,172,106,0.18)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>

    <Box>
      <Typography
        component={href ? "a" : "div"}
        href={href}
        sx={{
          color: "var(--color-text-primary)",
          fontSize: 15,
          fontWeight: 600,
          mb: 0.4,
          textDecoration: "none",
          display: "inline-block",
          "&:hover": { color: "var(--color-gold)" },
        }}
      >
        {title}
      </Typography>

      <Typography
        sx={{
          color: "var(--color-text-muted)",
          fontSize: 14,
          lineHeight: 1.7,
          maxWidth: 420,
        }}
      >
        {description}
      </Typography>
    </Box>
  </Box>
);

/* ---------------- INPUT STYLE ---------------- */

const luxuryInput = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "14px",
    backgroundColor: "#fff",
  },
  "& fieldset": {
    borderColor: "rgba(207,172,106,0.35)",
  },
  "& .MuiOutlinedInput-root:hover fieldset": {
    borderColor: "var(--color-gold)",
  },
  "& .MuiOutlinedInput-root.Mui-focused fieldset": {
    borderColor: "var(--color-gold)",
    boxShadow: "0 0 0 3px rgba(207,172,106,0.18)",
  },
  "& input, & textarea": {
    color: "var(--color-text-primary)",
  },
};

/* ---------------- FORM FIELDS ---------------- */

const fields = [
  { label: "Full Name", name: "name", type: "text" },
  { label: "Email Address", name: "email", type: "email" },
  { label: "Phone Number", name: "phone", type: "tel" },
];

export default function ContactHero() {
  const pageName = "Contact Page";

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async () => {
    setError("");
    setSuccess("");

    if (!formData.name || !formData.email || !formData.phone) {
      setError("Please fill all required fields.");
      return;
    }

    setLoading(true);

    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          access_key: "18ccc987-c5ed-437a-98f9-11f8e285b672",
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          message: formData.message,
          subject: `New Enquiry from ${pageName} – Vision X Nexus`,
        }),
      });

      const data = await res.json();

      if (data.success) {
        setSuccess("Thank you! Our team will contact you shortly.");
        setFormData({ name: "", email: "", phone: "", message: "" });
      } else {
        setError("Submission failed. Please try again.");
      }
    } catch {
      setError("Network error. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        pt: { xs: 12, md: 18 },
        pb: { xs: 10, md: 10},
        backgroundColor: "var(--color-bg)",
      }}
    >
      <Container maxWidth="xl">
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: { xs: 6, md: 10 },
            alignItems: "center",
          }}
        >
          {/* LEFT CONTENT */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Typography
              sx={{
                color: "var(--color-gold)",
                letterSpacing: "0.28em",
                fontSize: 12,
                mb: 3,
              }}
            >
              GET IN TOUCH
            </Typography>

            <Typography
              sx={{
                fontSize: { xs: "2.2rem", md: "3.4rem" },
                fontWeight: 600,
                mb: 3,
              }}
            >
              Speak To Our{" "}
              <Box component="span" sx={{ color: "var(--color-gold)" }}>
                Experts
              </Box>
            </Typography>

            <Typography
              sx={{
                color: "var(--color-text-muted)",
                fontSize: 16,
                lineHeight: 1.9,
                mb: 4,
              }}
            >
              Whether you’re looking to buy, rent, or invest, our consultants
              are here to guide you with clarity, market insight, and
              professionalism.
            </Typography>

            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 3, mb: 4 }}
            >
              <ContactItem
                icon={<PhoneOutlinedIcon sx={{ color: "var(--color-gold)" }} />}
                title="+971 54 791 4848"
                href="tel:+971547914848"
                description="Speak directly with our advisors"
              />

              <ContactItem
                icon={<EmailOutlinedIcon sx={{ color: "var(--color-gold)" }} />}
                title="info@visionxnexus.com"
                href="mailto:info@visionxnexus.com"
                description="Send us your enquiry anytime"
              />

              <ContactItem
                icon={
                  <LocationOnOutlinedIcon sx={{ color: "var(--color-gold)" }} />
                }
                title="Our Office"
                description="Bell Rashid Towers, Al Majaz 1, Sharjah, UAE"
              />
            </Box>

            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              {["Local Market Experts", "100% Client Confidentiality"].map(
                (item) => (
                  <Box
                    key={item}
                    sx={{
                      px: 2.6,
                      py: 1,
                      borderRadius: "30px",
                      border: "1px solid rgba(207,172,106,0.35)",
                      display: "flex",
                      alignItems: "center",
                      gap: 1.2,
                    }}
                  >
                    <VerifiedOutlinedIcon
                      sx={{ fontSize: 16, color: "var(--color-gold)" }}
                    />
                    <Typography
                      sx={{ color: "var(--color-text-primary)", fontSize: 12 }}
                    >
                      {item}
                    </Typography>
                  </Box>
                )
              )}
            </Box>
          </motion.div>

          {/* FORM */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <Box
              sx={{
                p: { xs: 3.5, md: 5 },
                borderRadius: 4,
                backgroundColor: "#fff",
                border: "1px solid rgba(207,172,106,0.45)",
                // boxShadow: "0 30px 80px rgba(0,0,0,0.15)",
              }}
            >
              <Typography
                sx={{
                  color: "var(--color-gold)",
                  fontSize: 18,
                  fontWeight: 600,
                  mb: 3,
                }}
              >
                Send Us a Message
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2.4 }}>
                {error && <Alert severity="error">{error}</Alert>}
                {success && <Alert severity="success">{success}</Alert>}

                {fields.map((field) => (
                  <TextField
                    key={field.name}
                    name={field.name}
                    type={field.type}
                    fullWidth
                    placeholder={field.label}
                    sx={luxuryInput}
                    value={formData[field.name]}
                    onChange={handleChange}
                  />
                ))}

                <TextField
                  name="message"
                  fullWidth
                  multiline
                  rows={4}
                  placeholder="Tell us about your requirement"
                  sx={luxuryInput}
                  value={formData.message}
                  onChange={handleChange}
                />
              </Box>

              <Button
                fullWidth
                onClick={handleSubmit}
                disabled={loading}
                sx={{
                  mt: 4,
                  py: 1.6,
                  borderRadius: "40px",
                  fontSize: 15,
                  fontWeight: 600,
                  backgroundColor: "var(--color-gold)",
                  color: "var(--color-text-primary)",
                  "&:hover": { transform: "translateY(-1px)" },
                }}
              >
                {loading ? <CircularProgress size={22} /> : "Send Message"}
              </Button>
            </Box>
          </motion.div>
        </Box>
      </Container>
    </Box>
  );
}
